import {
  lifecycleCodeFromRawAttribute,
  PRODUCT_LIFECYCLE_ATTRIBUTE_ID,
  REMOVED_FROM_SALE_LIFECYCLE_CODE,
  siteCodeFromLocale,
} from '@shared/types/product/regionAvailability';
import type { BadgeVariant } from '@/components/atoms/badge/types';

// EWM-192 / EWM-545 — parse the commercetools `productLifeCycle` variant
// attribute into its per-company lifecycle codes.
//
// CT stores it as a JSON-encoded array of
// `[{ company, values: { lifeCycleAttribute, ... } }, ...]`; codes are the
// conventional Jaycar/Electus single letters: "C" = Current, "Z"/"Zed" =
// Discontinued, "GO" = removed from sale, "N"/"P" = new/pending (awaiting
// first stock). The attribute is re-keyed under the `variants.attributes.`
// prefix like every other CT variant attribute.
//
// Shared so the PDP/PLP mapper (`map-product`) and the cart line-item mapper
// (`map-lineitem`) derive the same flags from the same source shape — the
// cart's "Stock due soon" label (EWM-545) must match the PDP's.
export const parseCtLifecycleCodes = (variantAttributes: Record<string, unknown>): string[] => {
  const raw = variantAttributes['variants.attributes.productLifeCycle'];
  if (typeof raw !== 'string' || !raw) return [];
  let entries: unknown;
  try {
    entries = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(entries)) return [];
  return entries
    .map((entry) => (entry as { values?: { lifeCycleAttribute?: unknown } } | null)?.values?.lifeCycleAttribute)
    .filter((code): code is string => typeof code === 'string');
};

// EWM-545 — `N` (new) / `P` (pending) products are new arrivals awaiting their
// first stock; when out of stock they read "Stock due soon" rather than
// "Out of stock".
export const isStockDueSoonEligibleFromCodes = (codes: string[]): boolean =>
  codes.some((code) => code === 'N' || code === 'P');

// EWM-241 / EWM-657 — the marketing badge is derived from the productLifeCycle
// code(s) per the Life Cycle Attribute list (OQ-16), in precedence order
// ON SPECIAL > CLEARANCE > NEW. Shared so the PLP/search path (Algolia, a single
// code) and the PDP path (commercetools, per-company codes) resolve the SAME
// badge — the PDP must show whatever the PLP shows for a given product.
const CLEARANCE_LIFECYCLE_CODES = new Set(['E', 'B', 'D', 'PD', 'SO', 'T']);
export const resolveBadgeFromLifecycleCodes = (codes: string[]): BadgeVariant | undefined => {
  if (codes.includes('PS')) return 'on-special'; // precedence 1
  if (codes.some((code) => CLEARANCE_LIFECYCLE_CODES.has(code))) return 'clearance'; // precedence 2
  if (codes.includes('N')) return 'new'; // precedence 3
  return undefined;
};

// EWM-944 — region-scoped "removed from sale" (`GO`) for a raw CT variant
// attribute bag.
//
// This exists because the aggregated `parseCtLifecycleCodes` above is
// deliberately CROSS-COMPANY (badges, stock-due-soon) and must not be used for
// the GO gate. GO is per-country: a SKU can be pulled in NZ and stay on sale in
// AU, and both storefronts ship from one deployment. Deriving it with
// `parseCtLifecycleCodes(...).includes('GO')` is the any-company `.some()` bug
// EWM-360/EWM-643 retired — it wrongly disabled AU Add-to-Cart when only NZ was
// GO — and it would also put the FE at odds with the BFF's own region-scoped
// gates in CartApi / WishlistApi.
//
// Same helper and rule as `map-product`, so every surface that needs the GO
// gate (PDP/PLP mapper, the CSV uploader, the header's SKU resolver) derives it
// identically instead of re-inlining the predicate.
export const isRemovedFromSaleForLocale = (
  variantAttributes: Record<string, unknown>,
  locale: string | undefined,
): boolean =>
  lifecycleCodeFromRawAttribute(variantAttributes[PRODUCT_LIFECYCLE_ATTRIBUTE_ID], siteCodeFromLocale(locale ?? '')) ===
  REMOVED_FROM_SALE_LIFECYCLE_CODE;
